import {getTestProps} from '@benchling/js-test-utils';
import {useTooltipInPortal} from '@visx/tooltip';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {DataType} from 'data-frames/DataFrameTypes';
import {DataPointColumnsMappedType} from 'data-frames/DataFrameTypes';
import {deserializeDecimalToFloat} from 'data-types/util/Deserializers';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import {NEAR_BLACK, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ellipsize} from 'util/NameUtil';
import omitKey from 'util/omitKey';

import {DEFAULT_LABEL_STYLE} from './utils/Theme';

export const TOOLTIP_LINE_HEIGHT = 14;
export const TOOLTIP_VERTICAL_PADDING = 4;
export const TOOLTIP_HORIZONTAL_PADDING = 6;
const MAX_AXIS_LABEL_LENGTH = 20;
const MAX_TOOLTIP_WIDTH = 400;

export interface LabelProps {
  color: string;
  text: string;
}

export interface TooltipInfo<DataPoint> {
  left: number;
  offsetLeft?: number;
  offsetTop?: number;
  label?: LabelProps;
  point: DataPoint;
  top: number;
  warning?: string;
}

export interface ChartTooltipProps<DataPoint> {
  axisTypes: DataPointColumnsMappedType<DataPoint, DataType>;
  columnNames: Array<keyof DataPoint>;
  columnLabels: DataPointColumnsMappedType<DataPoint, string>;
  tooltipInfo: TooltipInfo<DataPoint>;
}

export type UseChartTooltipInput<DataPoint> = Omit<ChartTooltipProps<DataPoint>, 'tooltipInfo'>;

interface UseChartTooltipOutput<DataPoint> {
  tooltipProps: ChartTooltipProps<DataPoint> | null;
  updateTooltipProps: (newTooltipInfo: TooltipInfo<DataPoint> | null) => void;
}

export function useChartTooltip<DataPoint>(
  fixedTooltipProperties: UseChartTooltipInput<DataPoint>
): UseChartTooltipOutput<DataPoint> {
  const [tooltipInfo, setTooltipInfo] = useState<TooltipInfo<DataPoint> | null>(null);

  return {
    tooltipProps:
      tooltipInfo != null
        ? {
            ...fixedTooltipProperties,
            tooltipInfo,
          }
        : null,
    updateTooltipProps: setTooltipInfo,
  };
}

export function estimateTooltipHeight(columnCount: number, hasLabel: boolean): number {
  return (columnCount + (hasLabel ? 1 : 0)) * TOOLTIP_LINE_HEIGHT + TOOLTIP_VERTICAL_PADDING * 2;
}

export default function ChartTooltip<DataPoint>(props: ChartTooltipProps<DataPoint>): JSX.Element {
  // Below two calls are copied from XYCharts Tooltip component. However, that tooltip does not handle points
  // that share the same x-value well, so we re-implemented the component on our own.
  // It works by:
  //   1. Setting up a portal to render the tooltip in
  //   2. Setting up a ref to an invisible svg element, through which we can get the parent element so the
  //      portal can compute its bounds
  const {containerRef, TooltipInPortal} = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });
  const setContainerRef = useCallback(
    (ownRef: HTMLElement | SVGElement | null) => {
      containerRef(ownRef?.parentElement ?? null);
    },
    [containerRef]
  );

  const {
    tooltipInfo: {point, label, warning, ...tooltipPosition},
  } = props;

  return (
    <>
      <svg ref={setContainerRef} />
      <TooltipInPortal {...tooltipPosition}>
        <ChartTooltipContents
          {...omitKey(props, 'tooltipInfo')}
          point={point}
          label={label}
          warning={warning}
        />
      </TooltipInPortal>
    </>
  );
}

export type ChartTooltipContentsProps<DataPoint> = Omit<ChartTooltipProps<DataPoint>, 'tooltipInfo'> & {
  label?: LabelProps;
  point: DataPoint;
  warning?: string;
};

export function ChartTooltipContents<DataPoint>(props: ChartTooltipContentsProps<DataPoint>): JSX.Element {
  const {axisTypes, columnLabels, columnNames, label, point, warning} = props;
  return (
    <div
      className={css(styles.tooltipContents)}
      {...getTestProps({component: 'ChartTooltipContents', element: 'root'})}
    >
      {columnNames.map((columnName, index) => (
        <AxisValue
          axisLabel={columnLabels[columnName]}
          axisType={axisTypes[columnName]}
          key={index}
          value={point[columnName]}
        />
      ))}
      {label != null && <LabelValue {...label} />}
      {warning != null && <WarningValue warning={warning} />}
    </div>
  );
}

export function AxisValue({
  axisLabel,
  axisType,
  value,
}: {
  axisLabel: string;
  axisType: DataType;
  value: unknown;
}): JSX.Element {
  let valueString = value ?? 'null';
  if (axisType === DataType.NUMBER && value != null) {
    const number = typeof value === 'string' ? deserializeDecimalToFloat(value) : (value as number);
    valueString = prettyChartNumber(number, true);
  }

  return (
    <div className="u-ellipsis-text">
      {ellipsize(axisLabel, MAX_AXIS_LABEL_LENGTH)}: {valueString}
    </div>
  );
}

export function LabelValue({color, text}: {color: string; text: string}): JSX.Element {
  return (
    <div
      className="u-flex-display"
      {...getTestProps({component: 'ChartTooltipContents', element: 'labelValue'})}
    >
      <span
        className={css(styles.colorBlock)}
        style={{backgroundColor: color}}
        {...getTestProps({component: 'ChartTooltipContents', element: 'colorBlock'})}
      />
      <span className="u-ellipsis-text">{text}</span>
    </div>
  );
}

export function WarningValue({warning}: {warning: string}): JSX.Element {
  return (
    <div className={`u-flex-display ${css(styles.warning)}`}>
      <BenchlingIcon className={css(styles.warningIcon)} icon="revision" />
      <span>{warning}</span>
    </div>
  );
}

const styles = StyleSheet.create({
  colorBlock: {
    flexShrink: 0,
    height: 10,
    width: 10,
    marginRight: 3,
    marginTop: 1,
  },
  tooltipContents: {
    ...omitKey(DEFAULT_LABEL_STYLE, 'fill'),
    ...FONT_PROXIMA_12,
    color: NEAR_BLACK,
    lineHeight: `${TOOLTIP_LINE_HEIGHT}px`,
    maxWidth: MAX_TOOLTIP_WIDTH,
    padding: `${TOOLTIP_VERTICAL_PADDING}px ${TOOLTIP_HORIZONTAL_PADDING}px`,
  },
  warning: {
    color: RED,
  },
  warningIcon: {
    marginRight: 3,
    marginTop: 1,
  },
});
